<template>
  <v-navigation-drawer
    app
    permanent
    floating
    class="drawer-wrapper"
    :mini-variant="isCollapsed"
    mini-variant-width="64"
    width="200"
  >
    <!-- Logo pinned above the scrolling list. The wordmark does not fit the
         64px rail, so the square mark stands in when collapsed. -->
    <template v-slot:prepend>
      <div class="drawer-brand text-center">
        <!-- The on-dark variant: the same artwork with the lettering in white
             and the orange mark untouched. A separate asset rather than a CSS
             filter, because a filter cannot recolour one part of an SVG and
             would flatten the orange too. The original stays in use on the
             login page, where it sits on a white card. -->
        <v-img
          v-if="!isCollapsed"
          class="mx-auto"
          src="@/assets/images/KarroFin-on-dark.svg"
          width="130px"
        ></v-img>
        <v-img
          v-else
          class="mx-auto"
          :src="mark_src"
          width="28px"
        ></v-img>
      </div>
    </template>

    <v-list class="pa-0 drawer_list py-0 drawer-list-wrapper" dense nav>
      <template v-for="(item, i) in visibleItems">
        <!-- Group break. Suppressed for the first group, so the only rule in
             the sidebar is the one above "Operations". -->
        <div v-if="item.first_in_group && i > 0" :key="'group-' + i">
          <v-divider class="mt-3 mb-1 drawer-divider"></v-divider>
          <v-subheader v-if="!isCollapsed" class="drawer-subheader">
            {{ item.group }}
          </v-subheader>
        </div>

        <v-tooltip :key="i" right :disabled="!isCollapsed">
          <template v-slot:activator="{ on, attrs }">
            <v-list-item
              :to="item.url"
              class="pa-0 mt-2 drawer-item"
              link
              v-bind="attrs"
              v-on="on"
              @click="setActiveTab(item)"
            >
              <v-list-item-icon class="drawer-icon">
                <LucideIcon :name="item.icon" :size="20" />
              </v-list-item-icon>
              <v-list-item-content class="py-0">
                <v-list-item-title class="drawer-text">
                  {{ item.label }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <span>{{ item.label }}</span>
        </v-tooltip>
      </template>
    </v-list>

    <!--   logout   -->
    <template v-slot:append>
      <div class="pa-2 drawer-append">
        <v-btn
          v-if="!isCollapsed"
          block
          outlined
          class="drawer-logout text-none"
          @click="logout"
        >
          <LucideIcon name="external-link" :size="16" class="mr-2" />
          {{ $lang.LOGOUT }}
        </v-btn>
        <v-tooltip v-else right>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              class="d-block mx-auto drawer-logout drawer-logout--icon"
              v-bind="attrs"
              v-on="on"
              @click="logout"
            >
              <LucideIcon name="external-link" :size="20" />
            </v-btn>
          </template>
          <span>{{ $lang.LOGOUT }}</span>
        </v-tooltip>
      </div>
    </template>
  </v-navigation-drawer>
</template>
<script>
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      warehouse: null,
      loading: false,
      // Served from public/, so it is referenced by URL rather than imported.
      mark_src: (process.env.BASE_URL || "/") + "favicon-2.svg",
    };
  },
  computed: {
    ...mapGetters({
      getDrawerItems: "drawer/getDrawerItems",
      getManageTab: "drawer/getManageTab",
      isCollapsed: "drawer/isCollapsed",
      getPermissionList: "getPermissionList",
    }),
    /**
     * The entries this user may see, each flagged when it opens a new group.
     * The flag is computed after filtering so the "Operations" rule follows
     * whichever entry is actually first, not whichever is first in the array.
     */
    visibleItems() {
      let previous_group = null;
      return this.getDrawerItems
        .filter(
          (item) =>
            !item.is_django_tab &&
            this.getPermissionList.includes(item.permission)
        )
        .map((item) => {
          const first_in_group = item.group !== previous_group;
          previous_group = item.group;
          return Object.assign({}, item, { first_in_group });
        });
    },
  },
  created() {
    this.$store.dispatch(
      "drawer/setActiveTab",
      localStorage.getItem("active-tab")
    );
  },
  methods: {
    setActiveTab(item) {
      this.$store.dispatch("drawer/setActiveTab", item.url.name);
      // clearing search text on tab change
      this.$store.dispatch("setSearchText", "");
    },
    logout() {
      this.request_POST(self, this.$urls.LOGOUT_TEAMMATE);
      localStorage.removeItem("active-tab");
      sessionStorage.clear();
      this.$router.push({ name: "login" });
    },
  },
};
</script>
<style scoped>
.drawer-wrapper {
  direction: rtl;

  /*
   * A dark panel, not another white surface.
   *
   * The drawer used to be #ffffff -- the same white as the top bar and every
   * card -- so white ran unbroken from the navigation into the content with
   * only a 1px hairline between them, and a permanent Vuetify drawer gets no
   * elevation to help. Painting the navigation dark makes it a region rather
   * than a strip, and `floating` suppresses Vuetify's own __border div so
   * there is exactly one edge instead of two stacked ones.
   */
  background: var(--kf-sidebar) !important;
}
/* The rtl above only exists to put the scroll bar on the left edge; everything
   inside it reads left to right. */
.drawer-list-wrapper,
.drawer-brand,
.drawer-append {
  direction: ltr;
}
/* The default scrollbar is a bright strip down a dark panel. */
.drawer-wrapper ::-webkit-scrollbar {
  width: 6px;
}
.drawer-wrapper ::-webkit-scrollbar-thumb {
  background: var(--kf-sidebar-border);
  border-radius: 3px;
}
.drawer-wrapper ::-webkit-scrollbar-track {
  background: transparent;
}
.drawer-brand {
  padding: 20px 8px 14px;
  border-bottom: 1px solid var(--kf-sidebar-border);
  margin-bottom: 6px;
}
/* No filter on the logo: the sidebar uses the on-dark artwork instead, so the
   lettering is white (12.8:1 on this navy) while the orange mark keeps its
   brand colour. */
.drawer_list {
  padding: 0 8px !important;
}
/*
 * Colour lives on the ROW, not on the label and icon separately.
 *
 * LucideIcon strokes with `currentColor`, so one declaration here drives the
 * icon and the text together in every state. The rule this replaces targeted
 * `.drawer-icon .v-icon`, which has matched nothing since the sidebar moved to
 * Lucide -- harmless on a white panel, where icons inherited a dark default
 * that happened to look right, and fatal on a dark one, where they would have
 * been dark-on-dark.
 */
.drawer-item {
  min-height: 40px;
  border-radius: 8px;
  color: var(--kf-sidebar-text) !important;
  transition: background 0.15s ease, color 0.15s ease;
}
.drawer-item:hover {
  height: fit-content !important;
  background: var(--kf-sidebar-hover);
  color: var(--kf-sidebar-text-strong) !important;
}
/* "You are here": a lighter fill plus a 3px orange bar on the leading edge --
   the one bright thing on the panel, and it is the app's own CTA colour. */
.drawer_list .v-list-item--active {
  background: var(--kf-sidebar-active) !important;
  height: fit-content !important;
  border: none !important;
  border-left: 3px solid var(--kf-sidebar-accent) !important;
  border-radius: 8px;
  color: var(--kf-sidebar-text-strong) !important;
}
/* The weight has to sit on the label, not on the row: .drawer-text declares its
   own font-weight, and a descendant's own declaration beats an inherited one
   no matter how important the ancestor's is. */
.drawer_list .v-list-item--active .drawer-text {
  font-weight: 800 !important;
  color: var(--kf-sidebar-text-strong);
}
/* Vuetify tints an active v-list-item via a ::before overlay, which on a dark
   fill shows up as a milky wash over the row. */
.drawer_list .v-list-item--active::before {
  opacity: 0 !important;
}
.drawer-text {
  color: inherit;
  /* !important because Vuetify's `.v-list--dense .v-list-item
     .v-list-item__title` is a three-class selector and beats this scoped one,
     pinning the label at 13px. Without it the size set here does nothing. */
  font-size: 14.5px !important;
  font-weight: 700 !important;
}
.drawer-logout {
  color: var(--kf-sidebar-text) !important;
  border-color: var(--kf-sidebar-border) !important;
  font-weight: 700;
  letter-spacing: 0;
}
/* Destructive on intent, quiet at rest. */
.drawer-logout:hover {
  color: #ff8a7a !important;
  border-color: #ff8a7a !important;
  background: rgba(255, 138, 122, 0.08) !important;
}
.drawer-logout--icon {
  border: none !important;
}
.drawer-subheader {
  height: 24px;
  padding: 0 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  /* Present but quiet: it is a signpost between two groups, not a nav item. */
  color: var(--kf-sidebar-text);
  opacity: 0.62;
}
.drawer-divider {
  border-color: var(--kf-sidebar-border) !important;
}
</style>
<style>
/* Icon column: Vuetify's default 32px right margin pushes the label off a
   200px drawer, and in mini mode the icon has to sit centred on the rail. */
.drawer_list .drawer-item .drawer-icon {
  margin: 8px 12px 8px 10px !important;
  align-self: center;
}
.v-navigation-drawer--mini-variant .drawer_list .drawer-item .drawer-icon {
  margin: 8px 0 !important;
}
.v-navigation-drawer--mini-variant .drawer_list {
  padding: 0 4px !important;
}
</style>
